<template>
  <div id="app">
    <div v-if="!token" class="login-wrap">
      <section class="login-brand-panel">
        <div class="brand-center">
          <div class="brand-emblem">
            <span></span>
          </div>
          <h1>鼎腾后台系统</h1>
          <div class="brand-subtitle">WORKSHOP CONTROL SYSTEM</div>
          <div class="brand-divider"></div>
          <div class="brand-features">
            <div class="brand-feature">
              <div class="feature-icon">分</div>
              <p>分类管理</p>
            </div>
            <div class="brand-feature">
              <div class="feature-icon">件</div>
              <p>零件维护</p>
            </div>
            <div class="brand-feature">
              <div class="feature-icon">数</div>
              <p>数据联动</p>
            </div>
          </div>
        </div>
      </section>
      <section class="login-form-panel">
        <div class="login-card">
          <h2>进入系统</h2>
          <p class="login-subtitle">输入账号与密码，即刻开始工作</p>
          <div class="login-state" :class="{ error: errorMsg }">{{ errorMsg || '欢迎回来，请登录' }}</div>
          <div class="login-field">
            <label>用户名</label>
            <input v-model.trim="loginForm.username" placeholder="请输入用户名" autocomplete="username" />
          </div>
          <div class="login-field">
            <label>密码</label>
            <input v-model="loginForm.password" type="password" placeholder="请输入密码" autocomplete="current-password" @keyup.enter="handleLogin" />
          </div>
          <button class="login-button" :disabled="loading.login" @click="handleLogin">{{ loading.login ? '登录中...' : '进入工作台' }}</button>
        </div>
      </section>
    </div>

    <div v-else class="layout">
      <aside class="sidebar">
        <div class="brand">
          <span class="brand-mark">DT</span>
          <span>
            <strong>鼎腾后台系统</strong>
            <small>零件询价管理平台</small>
          </span>
        </div>
        <div class="menu" :class="{ active: activeMenu === 'home' }" @click="switchMenu('home')"><span class="menu-icon">◆</span><span>首页</span></div>
        <div class="menu" :class="{ active: activeMenu === 'category' }" @click="switchMenu('category')"><span class="menu-icon">▦</span><span>零件分类</span></div>
        <div class="menu" :class="{ active: activeMenu === 'part' }" @click="switchMenu('part')"><span class="menu-icon">▣</span><span>零件管理</span></div>
      </aside>
      <main class="content">
        <div class="topbar">
          <div class="page-title">{{ activeMenuTitle }}</div>
          <div class="topbar-user">
            <span>{{ currentDateText }}</span>
            <span class="user-pill">{{ admin.real_name || admin.username || '管理员' }}</span>
            <button @click="logout">退出登录</button>
          </div>
        </div>

        <section v-if="activeMenu === 'home'" class="panel">
          <h3>首页</h3>
          <div class="stats-grid">
            <div class="stat-card">
              <span>分类数量</span>
              <strong>{{ dashboardStats.categoryTotal }}</strong>
            </div>
            <div class="stat-card">
              <span>零件数量</span>
              <strong>{{ dashboardStats.partTotal }}</strong>
            </div>
          </div>
        </section>

        <section v-if="activeMenu === 'category'" class="panel">
          <h3>零件分类</h3>
          <div class="toolbar">
            <input v-model.trim="categoryQuery.keyword" placeholder="分类名称关键字" />
            <select v-model="categoryQuery.level">
              <option value="">全部级别</option>
              <option :value="1">一级分类</option>
              <option :value="2">二级分类</option>
              <option :value="3">三级分类</option>
            </select>
            <select v-model="categoryQuery.parent_id">
              <option value="">全部父分类</option>
              <option :value="0">一级分类</option>
              <option v-for="c in parentCategoryOptions" :key="`parent-${c.id}`" :value="c.id">{{ c.full_name || c.name }} 下级</option>
            </select>
            <select v-model="categoryQuery.status">
              <option value="">全部状态</option>
              <option :value="1">启用</option>
              <option :value="0">停用</option>
            </select>
            <button @click="searchCategories">查询</button>
            <button class="primary" @click="openCategoryModal()">新增分类</button>
          </div>

          <table>
            <thead>
              <tr><th>ID</th><th>分类路径</th><th>层级</th><th>父分类</th><th>排序</th><th>状态</th><th>操作</th></tr>
            </thead>
            <tbody>
              <tr v-for="item in categories" :key="item.id">
                <td>{{ item.id }}</td>
                <td>{{ item.full_name || item.name }}</td>
                <td><span class="category-level-badge" :class="`level-${Number(item.level || 1)}`">{{ levelText(item.level) }}</span></td>
                <td>{{ item.parent_name || '-' }}</td>
                <td>{{ item.sort_order }}</td>
                <td>{{ item.status === 1 ? '启用' : '停用' }}</td>
                <td>
                  <div class="row-actions">
                    <button @click="openCategoryModal(item)">编辑</button>
                    <button :disabled="Number(item.level || 1) >= 3" @click="openQuickCategoryModal(item)">新增下级</button>
                    <button class="danger" @click="deleteCategory(item.id)">删除</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="pagination">
            <span>共 {{ categoryPager.total }} 条</span>
            <select v-model.number="categoryPager.pageSize" @change="changeCategoryPageSize">
              <option :value="10">10条/页</option>
              <option :value="20">20条/页</option>
              <option :value="50">50条/页</option>
            </select>
            <button :disabled="categoryPager.page <= 1" @click="changeCategoryPage(categoryPager.page - 1)">上一页</button>
            <span>{{ categoryPager.page }} / {{ categoryTotalPages }}</span>
            <button :disabled="categoryPager.page >= categoryTotalPages" @click="changeCategoryPage(categoryPager.page + 1)">下一页</button>
          </div>
        </section>

        <section v-if="activeMenu === 'part'" class="panel">
          <h3>零件管理</h3>
          <div class="toolbar">
            <input v-model.trim="partQuery.keyword" placeholder="关键字" />
            <select v-model="partQuery.category_id">
              <option value="">全部分类</option>
              <option v-for="c in partFilterOptions" :key="`part-filter-${c.id}`" :value="c.id">{{ c.name }}</option>
            </select>
            <select v-model="partQuery.status">
              <option value="">全部状态</option>
              <option :value="1">启用</option>
              <option :value="0">停用</option>
            </select>
            <button @click="searchParts">查询</button>
            <button class="primary" @click="openPartModal()">新增零件</button>
          </div>

          <table>
            <thead>
              <tr>
                <th>ID</th><th>图片</th><th>视频</th><th>分类路径</th><th>编号</th><th>名称</th><th>规格参数</th><th>价格</th><th>重量</th><th>状态</th><th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in parts" :key="item.id">
                <td>{{ item.id }}</td>
                <td>
                  <div v-if="item.image_url" class="thumb-list">
                    <img
                      v-for="(url, idx) in item.image_url.split(',').filter(Boolean)"
                      :key="idx"
                      class="part-thumb clickable-thumb"
                      :src="resolveAssetUrl(url)"
                      alt="零件图片"
                      @click="openImagePreview(resolveAssetUrl(url), item.image_url.split(',').filter(Boolean).map(resolveAssetUrl))"
                    />
                  </div>
                  <span v-else>-</span>
                </td>
                <td>
                  <button v-if="item.video_url" @click="openVideoPreview(item.video_url)">查看视频</button>
                  <span v-else>-</span>
                </td>
                <td>{{ item.category_full_name || item.category_name }}</td>
                <td>{{ item.part_code }}</td>
                <td>{{ item.part_name }}</td>
                <td>{{ item.spec_param || '-' }}</td>
                <td>{{ item.price }}</td>
                <td>{{ item.weight || 0 }}</td>
                <td>{{ item.status === 1 ? '启用' : '停用' }}</td>
                <td>
                  <div class="row-actions">
                    <button @click="openPartModal(item)">编辑</button>
                    <button class="danger" @click="deletePart(item.id)">删除</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="pagination">
            <span>共 {{ partPager.total }} 条</span>
            <select v-model.number="partPager.pageSize" @change="changePartPageSize">
              <option :value="10">10条/页</option>
              <option :value="20">20条/页</option>
              <option :value="50">50条/页</option>
            </select>
            <button :disabled="partPager.page <= 1" @click="changePartPage(partPager.page - 1)">上一页</button>
            <span>{{ partPager.page }} / {{ partTotalPages }}</span>
            <button :disabled="partPager.page >= partTotalPages" @click="changePartPage(partPager.page + 1)">下一页</button>
          </div>
        </section>
      </main>
    </div>

    <div v-if="showCategoryModal" class="modal-mask" @click.self="closeCategoryModal">
      <div class="modal">
        <h4>{{ categoryForm.id ? '编辑分类' : '新增分类' }}</h4>
        <div v-if="quickCategoryParent" class="modal-form-item">
          <label>父分类</label>
          <div class="readonly-text">{{ quickCategoryParent.full_name || quickCategoryParent.name }}</div>
        </div>
        <div v-else class="modal-form-item">
          <label>父分类</label>
          <select v-model="categoryForm.parent_id">
            <option :value="null">无（一级分类）</option>
            <option v-for="c in selectableParentCategories" :key="`pc-${c.id}`" :value="c.id">{{ c.full_name || c.name }}</option>
          </select>
        </div>
        <div class="modal-form-item">
          <label>分类名称</label>
          <input v-model.trim="categoryForm.name" />
        </div>
        <div v-if="!quickCategoryParent" class="modal-form-item">
          <label>排序</label>
          <input v-model.number="categoryForm.sort_order" type="number" />
        </div>
        <div v-if="!quickCategoryParent" class="modal-form-item">
          <label>状态</label>
          <select v-model.number="categoryForm.status"><option :value="1">启用</option><option :value="0">停用</option></select>
        </div>
        <div class="modal-actions">
          <button @click="closeCategoryModal">取消</button>
          <button class="primary" @click="submitCategory">保存</button>
        </div>
      </div>
    </div>

    <div v-if="showPartModal" class="modal-mask" @click.self="closePartModal">
      <div class="modal modal-wide">
        <h4>{{ partForm.id ? '编辑零件' : '新增零件' }}</h4>
        <div class="modal-form-grid">
          <div class="modal-form-item modal-form-full category-picker-item">
            <label>所属分类</label>
            <div class="category-picker">
              <div class="category-picker-level">
                <span>一级</span>
                <button
                  v-for="c in partCategoryRootOptions"
                  :key="`root-${c.id}`"
                  :class="{ active: Number(partSelectedRootId) === Number(c.id), selected: Number(partForm.category_id) === Number(c.id) }"
                  @click="selectPartCategory(c)"
                >{{ c.name }}</button>
              </div>
              <div v-if="partSecondCategoryOptions.length" class="category-picker-level">
                <span>二级</span>
                <button
                  v-for="c in partSecondCategoryOptions"
                  :key="`second-${c.id}`"
                  :class="{ active: Number(partSelectedSecondId) === Number(c.id), selected: Number(partForm.category_id) === Number(c.id) }"
                  @click="selectPartCategory(c)"
                >{{ c.name }}</button>
              </div>
              <div v-if="partThirdCategoryOptions.length" class="category-picker-level">
                <span>三级</span>
                <button
                  v-for="c in partThirdCategoryOptions"
                  :key="`third-${c.id}`"
                  :class="{ selected: Number(partForm.category_id) === Number(c.id) }"
                  @click="selectPartCategory(c)"
                >{{ c.name }}</button>
              </div>
              <div class="category-picked">当前选择：{{ partSelectedCategoryName || '请选择分类' }}</div>
            </div>
          </div>
          <div class="modal-form-item">
            <label>零件编号</label>
            <div class="readonly-text">{{ partForm.part_code || '保存后自动生成' }}</div>
          </div>
          <div class="modal-form-item">
            <label>零件名称</label>
            <input v-model.trim="partForm.part_name" />
          </div>
          <div class="modal-form-item">
            <label>规格参数</label>
            <input v-model.trim="partForm.spec_param" />
          </div>
          <div class="modal-form-item">
            <label>单位</label>
            <input v-model.trim="partForm.unit" />
          </div>
          <div class="modal-form-item">
            <label>价格</label>
            <input v-model.number="partForm.price" type="number" />
          </div>
          <div class="modal-form-item">
            <label>重量</label>
            <input v-model.number="partForm.weight" type="number" min="0" step="0.01" />
          </div>
          <div class="modal-form-item">
            <label>状态</label>
            <select v-model.number="partForm.status"><option :value="1">启用</option><option :value="0">停用</option></select>
          </div>
          <div class="modal-form-item modal-form-full">
            <label>零件图片</label>
            <div class="image-picker" style="align-items: flex-start; flex-wrap: wrap;">
              <div v-for="(url, idx) in partForm.image_urls" :key="idx" class="image-preview-wrap">
                <img class="image-preview" :src="resolveAssetUrl(url)" alt="零件图片预览" @click="openImagePreview(resolveAssetUrl(url))" />
                <span class="image-remove" @click="removePartImage(idx)">×</span>
              </div>
              <label class="upload-button" style="margin-top: 4px;">
                添加图片
                <input type="file" accept="image/*" @change="onPartImageChange" />
              </label>
              <span v-if="!partForm.image_urls.length" class="upload-name">未上传图片</span>
              <span v-if="partUploadProgress > 0 && partUploadProgress < 100" class="upload-name">上传中 {{ partUploadProgress }}%</span>
            </div>
          </div>
          <div class="modal-form-item modal-form-full">
            <label>零件视频</label>
            <div class="image-picker">
              <label class="upload-button">
                选择视频
                <input type="file" accept="video/*" @change="onPartVideoChange" />
              </label>
              <span class="upload-name">{{ partForm.video_file ? partForm.video_file.name : (partForm.video_url ? '已上传视频' : '未选择视频') }}</span>
              <button v-if="partForm.video_url" @click="openVideoPreview(partForm.video_url)">查看视频</button>
              <span class="upload-hint">视频大小最高 500M</span>
            </div>
          </div>
          <div v-if="partUploadProgress > 0 && partUploadProgress < 100" class="modal-form-item modal-form-full">
            <label>上传进度</label>
            <div class="upload-progress">
              <div class="upload-progress-bar"><span :style="{ width: `${partUploadProgress}%` }"></span></div>
              <em>{{ partUploadProgress }}%</em>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button @click="closePartModal">取消</button>
          <button class="primary" :disabled="partVideoUploading" @click="submitPart">{{ partVideoUploading ? '视频上传中...' : '保存' }}</button>
        </div>
      </div>
    </div>

    <div v-if="previewImageUrls.length" class="modal-mask image-preview-mask" @click.self="closeImagePreview">
      <div class="image-preview-modal">
        <button class="image-preview-close" @click="closeImagePreview">×</button>
        <div class="image-preview-nav" v-if="previewImageUrls.length > 1">
          <button class="image-preview-prev" :disabled="previewImageIndex <= 0" @click="previewImageIndex--">‹</button>
          <span class="image-preview-count">{{ previewImageIndex + 1 }} / {{ previewImageUrls.length }}</span>
          <button class="image-preview-next" :disabled="previewImageIndex >= previewImageUrls.length - 1" @click="previewImageIndex++">›</button>
        </div>
        <img :src="previewImageUrls[previewImageIndex]" alt="零件图片预览" />
      </div>
    </div>

    <div v-if="previewVideoUrl" class="modal-mask image-preview-mask" @click.self="closeVideoPreview">
      <div class="video-preview-modal">
        <button class="image-preview-close" @click="closeVideoPreview">×</button>
        <video :src="previewVideoUrl" controls autoplay></video>
      </div>
    </div>

    <transition name="fade-pop">
      <div v-if="notice.show" class="notice" :class="notice.type">
        <div class="notice-title">{{ notice.type === 'success' ? '操作成功' : '操作失败' }}</div>
        <div class="notice-text">{{ notice.message }}</div>
      </div>
    </transition>

    <div v-if="confirmDialog.show" class="modal-mask" @click.self="hideConfirm">
      <div class="modal confirm-modal">
        <h4>操作确认</h4>
        <p class="confirm-text">{{ confirmDialog.message }}</p>
        <div class="modal-actions">
          <button @click="hideConfirm">取消</button>
          <button class="primary" @click="onConfirmOk">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const API_BASE = '';

const defaultCategoryForm = () => ({ id: null, parent_id: null, name: '', sort_order: 0, status: 1 });
const defaultPartForm = () => ({
  id: null,
  category_id: null,
  part_code: '',
  part_name: '',
  spec_param: '',
  unit: '件',
  price: '',
  weight: '',
  image_url: '',
  image_urls: [],
  image_file: null,
  video_url: '',
  video_file: null,
  status: 1
});

export default {
  name: 'App',
  data() {
    return {
      token: localStorage.getItem('admin_token') || '',
      admin: JSON.parse(localStorage.getItem('admin_info') || '{}'),
      activeMenu: 'home',
      loading: { login: false },
      errorMsg: '',
      loginForm: { username: '', password: '' },
      categories: [],
      categoryOptions: [],
      partFilterOptions: [],
      dashboardStats: { categoryTotal: 0, partTotal: 0 },
      categoryQuery: { keyword: '', level: '', parent_id: '', status: '' },
      categoryPager: { page: 1, pageSize: 10, total: 0 },
      categoryForm: defaultCategoryForm(),
      quickCategoryParent: null,
      showCategoryModal: false,
      parts: [],
      partQuery: { keyword: '', category_id: '', status: '' },
      partPager: { page: 1, pageSize: 10, total: 0 },
      partForm: defaultPartForm(),
      partImagePreview: '',
      partUploadProgress: 0,
      partVideoUploading: false,
      previewImageUrls: [],
      previewImageIndex: 0,
      previewVideoUrl: '',
      showPartModal: false,
      notice: { show: false, type: 'success', message: '' },
      confirmDialog: { show: false, message: '', resolve: null }
    };
  },
  mounted() {
    if (this.token) this.bootstrap();
  },
  computed: {
    parentCategoryOptions() {
      return this.categoryOptions.filter((item) => Number(item.level || 1) < 3);
    },
    selectableParentCategories() {
      return this.parentCategoryOptions.filter((item) => item.id !== this.categoryForm.id);
    },
    partFormCategoryOptions() {
      return this.categoryOptions;
    },
    partCategoryRootOptions() {
      return this.categoryOptions.filter((item) => Number(item.level || 1) === 1);
    },
    partSelectedRootId() {
      const selected = this.getCategoryById(this.partForm.category_id);
      if (!selected) return null;
      if (Number(selected.level || 1) === 1) return selected.id;
      if (Number(selected.level || 1) === 2) return selected.parent_id;
      const parent = this.getCategoryById(selected.parent_id);
      return selected.grandparent_id || (parent ? parent.parent_id : null);
    },
    partSelectedSecondId() {
      const selected = this.getCategoryById(this.partForm.category_id);
      if (!selected) return null;
      if (Number(selected.level || 1) === 2) return selected.id;
      if (Number(selected.level || 1) === 3) return selected.parent_id;
      return null;
    },
    partSecondCategoryOptions() {
      if (!this.partSelectedRootId) return [];
      return this.categoryOptions.filter((item) => Number(item.parent_id) === Number(this.partSelectedRootId));
    },
    partThirdCategoryOptions() {
      if (!this.partSelectedSecondId) return [];
      return this.categoryOptions.filter((item) => Number(item.parent_id) === Number(this.partSelectedSecondId));
    },
    partSelectedCategoryName() {
      const selected = this.getCategoryById(this.partForm.category_id);
      return selected ? selected.full_name || selected.name : '';
    },
    activeMenuTitle() {
      const titles = { home: '总览看板', category: '零件分类', part: '零件管理' };
      return titles[this.activeMenu] || '后台管理';
    },
    currentDateText() {
      const now = new Date();
      return `${now.getFullYear()}/${now.getMonth() + 1}/${now.getDate()}`;
    },
    categoryTotalPages() {
      return Math.max(1, Math.ceil(this.categoryPager.total / this.categoryPager.pageSize));
    },
    partTotalPages() {
      return Math.max(1, Math.ceil(this.partPager.total / this.partPager.pageSize));
    }
  },
  methods: {
    async request(path, options = {}) {
      const isFormData = options.body instanceof FormData;
      const headers = { ...(options.headers || {}) };
      if (!isFormData) headers['Content-Type'] = 'application/json';
      if (this.token) headers.Authorization = `Bearer ${this.token}`;
      const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
      const json = await res.json();
      if (!res.ok || json.code !== 0) {
        if (res.status === 401) this.logout();
        throw new Error(json.message || '请求失败');
      }
      return json.data;
    },
    async handleLogin() {
      this.errorMsg = '';
      if (!this.loginForm.username || !this.loginForm.password) return (this.errorMsg = '请输入账号和密码');
      this.loading.login = true;
      try {
        const data = await this.request('/api/admin/login', { method: 'POST', body: JSON.stringify(this.loginForm) });
        this.token = data.token;
        this.admin = data.admin || {};
        localStorage.setItem('admin_token', this.token);
        localStorage.setItem('admin_info', JSON.stringify(this.admin));
        await this.bootstrap();
      } catch (e) {
        this.errorMsg = e.message;
      } finally {
        this.loading.login = false;
      }
    },
    async bootstrap() {
      await this.fetchCategoryOptions();
      await Promise.all([this.fetchCategories(), this.fetchParts(), this.fetchDashboardStats()]);
    },
    async switchMenu(menu) {
      this.activeMenu = menu;
      try {
        if (menu === 'category') {
          await Promise.all([this.fetchCategoryOptions(), this.fetchCategories()]);
        }
        if (menu === 'part') {
          await this.fetchCategoryOptions();
          await this.fetchParts();
        }
      } catch (e) {
        this.notify('error', e.message || '数据刷新失败');
      }
    },
    logout() {
      this.token = '';
      this.admin = {};
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_info');
      this.activeMenu = 'home';
    },
    async fetchCategoryOptions() {
      const data = await this.request('/api/admin/categories?page=1&page_size=500');
      const items = data.items || [];
      this.categoryOptions = items;
      this.partFilterOptions = items.map((item) => ({ id: item.id, name: item.full_name || item.name }));
    },
    async fetchDashboardStats() {
      const [categoryData, partData] = await Promise.all([
        this.request('/api/admin/categories?page=1&page_size=1'),
        this.request('/api/admin/parts?page=1&page_size=1')
      ]);
      this.dashboardStats = {
        categoryTotal: Number(categoryData.total || 0),
        partTotal: Number(partData.total || 0)
      };
    },
    async fetchCategories() {
      const q = new URLSearchParams({ page: this.categoryPager.page, page_size: this.categoryPager.pageSize });
      if (this.categoryQuery.keyword) q.set('keyword', this.categoryQuery.keyword);
      if (this.categoryQuery.level !== '') q.set('level', this.categoryQuery.level);
      if (this.categoryQuery.parent_id !== '') q.set('parent_id', this.categoryQuery.parent_id);
      if (this.categoryQuery.status !== '') q.set('status', this.categoryQuery.status);
      const data = await this.request(`/api/admin/categories?${q.toString()}`);
      this.categories = data.items || [];
      this.categoryPager.total = Number(data.total || 0);
      this.categoryPager.page = Number(data.page || this.categoryPager.page);
      this.categoryPager.pageSize = Number(data.page_size || this.categoryPager.pageSize);
    },
    async searchCategories() {
      this.categoryPager.page = 1;
      await this.fetchCategories();
    },
    async changeCategoryPage(page) {
      this.categoryPager.page = Math.max(1, Math.min(page, this.categoryTotalPages));
      await this.fetchCategories();
    },
    async changeCategoryPageSize() {
      this.categoryPager.page = 1;
      await this.fetchCategories();
    },
    openCategoryModal(item) {
      this.quickCategoryParent = null;
      this.categoryForm = item ? { ...item, parent_id: item.parent_id == null ? null : item.parent_id } : defaultCategoryForm();
      this.showCategoryModal = true;
    },
    openQuickCategoryModal(parent) {
      if (Number(parent.level || 1) >= 3) return;
      this.quickCategoryParent = parent;
      this.categoryForm = { ...defaultCategoryForm(), parent_id: parent.id };
      this.showCategoryModal = true;
    },
    closeCategoryModal() {
      this.showCategoryModal = false;
      this.quickCategoryParent = null;
      this.categoryForm = defaultCategoryForm();
    },
    async submitCategory() {
      const payload = {
        parent_id: this.categoryForm.parent_id === null || this.categoryForm.parent_id === '' ? null : Number(this.categoryForm.parent_id),
        name: this.categoryForm.name,
        sort_order: Number(this.categoryForm.sort_order || 0),
        status: Number(this.categoryForm.status)
      };
      if (!payload.name) return this.notify('error', '分类名称不能为空');
      try {
        if (this.categoryForm.id) {
          await this.request(`/api/admin/categories/${this.categoryForm.id}`, { method: 'PUT', body: JSON.stringify(payload) });
          this.notify('success', '分类更新成功');
        } else {
          await this.request('/api/admin/categories', { method: 'POST', body: JSON.stringify(payload) });
          this.notify('success', '分类新增成功');
        }
      } catch (e) {
        return this.notify('error', e.message || '分类保存失败');
      }
      this.closeCategoryModal();
      await Promise.all([this.fetchCategories(), this.fetchCategoryOptions(), this.fetchParts(), this.fetchDashboardStats()]);
    },
    async deleteCategory(id) {
      const ok = await this.confirm('确认删除该分类吗？');
      if (!ok) return;
      try {
        await this.request(`/api/admin/categories/${id}`, { method: 'DELETE' });
        this.notify('success', '分类删除成功');
        await Promise.all([this.fetchCategories(), this.fetchCategoryOptions(), this.fetchParts(), this.fetchDashboardStats()]);
      } catch (e) {
        this.notify('error', e.message || '分类删除失败');
      }
    },
    async fetchParts() {
      const q = new URLSearchParams({ page: this.partPager.page, page_size: this.partPager.pageSize });
      if (this.partQuery.keyword) q.set('keyword', this.partQuery.keyword);
      if (this.partQuery.category_id) q.set('category_id', this.partQuery.category_id);
      if (this.partQuery.status !== '') q.set('status', this.partQuery.status);
      const data = await this.request(`/api/admin/parts?${q.toString()}`);
      this.parts = data.items || [];
      this.partPager.total = Number(data.total || 0);
      this.partPager.page = Number(data.page || this.partPager.page);
      this.partPager.pageSize = Number(data.page_size || this.partPager.pageSize);
    },
    async searchParts() {
      this.partPager.page = 1;
      await this.fetchParts();
    },
    async changePartPage(page) {
      this.partPager.page = Math.max(1, Math.min(page, this.partTotalPages));
      await this.fetchParts();
    },
    async changePartPageSize() {
      this.partPager.page = 1;
      await this.fetchParts();
    },
    levelText(level) {
      return `${Number(level || 1)}级`;
    },
    getCategoryById(id) {
      if (id === null || id === undefined || id === '') return null;
      return this.categoryOptions.find((item) => Number(item.id) === Number(id)) || null;
    },
    selectPartCategory(category) {
      this.partForm.category_id = category.id;
    },
    openPartModal(item) {
      this.partForm = item
        ? {
            id: item.id,
            category_id: item.category_id,
            part_code: item.part_code || '',
            part_name: item.part_name || '',
            spec_param: item.spec_param || '',
            unit: item.unit || '件',
            price: item.price == null ? '' : item.price,
            weight: item.weight == null ? '' : item.weight,
            image_url: item.image_url || '',
            image_urls: item.image_url ? item.image_url.split(',').filter(Boolean) : [],
            image_file: null,
            video_url: item.video_url || '',
            video_file: null,
            status: item.status
          }
        : defaultPartForm();
      this.partImagePreview = '';
      this.partUploadProgress = 0;
      this.partVideoUploading = false;
      this.showPartModal = true;
    },
    closePartModal() {
      this.showPartModal = false;
      this.partForm = defaultPartForm();
      this.partImagePreview = '';
      this.partUploadProgress = 0;
      this.partVideoUploading = false;
    },
    async submitPart() {
      if (this.partVideoUploading) return this.notify('error', '请等待视频上传成功后再保存');
      if (!this.partForm.category_id || !this.partForm.part_name) return this.notify('error', '分类和零件名称不能为空');
      const selectedCategory = this.partFormCategoryOptions.find((item) => item.id === Number(this.partForm.category_id));
      if (!selectedCategory) return this.notify('error', '请选择有效分类');
      const payload = this.buildPartFormData();
      try {
        if (this.partForm.id) {
          await this.uploadPartForm(`/api/admin/parts/${this.partForm.id}`, 'PUT', payload);
          this.notify('success', '零件更新成功');
        } else {
          await this.uploadPartForm('/api/admin/parts', 'POST', payload);
          this.notify('success', '零件新增成功');
        }
      } catch (e) {
        return this.notify('error', e.message || '零件保存失败');
      }
      this.closePartModal();
      await Promise.all([this.fetchParts(), this.fetchDashboardStats()]);
    },
    buildPartFormData() {
      const formData = new FormData();
      formData.append('category_id', Number(this.partForm.category_id));
      formData.append('part_name', this.partForm.part_name);
      formData.append('spec_param', this.partForm.spec_param || '');
      formData.append('unit', this.partForm.unit || '件');
      formData.append('price', this.partForm.price || 0);
      formData.append('weight', this.partForm.weight || 0);
      formData.append('status', Number(this.partForm.status));
      formData.append('image_url', this.partForm.image_urls.join(','));
      if (this.partForm.video_url) {
        formData.append('video_url', this.partForm.video_url);
      }
      return formData;
    },
    uploadPartForm(path, method, formData) {
      this.partUploadProgress = 0;
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, `${API_BASE}${path}`);
        if (this.token) xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;
          this.partUploadProgress = Math.max(1, Math.min(99, Math.round((event.loaded / event.total) * 100)));
        };
        xhr.onload = () => {
          let json = {};
          try {
            json = JSON.parse(xhr.responseText || '{}');
          } catch (e) {
            reject(new Error('服务器响应格式错误'));
            return;
          }
          if (xhr.status < 200 || xhr.status >= 300 || json.code !== 0) {
            if (xhr.status === 401) this.logout();
            reject(new Error(json.message || '请求失败'));
            return;
          }
          this.partUploadProgress = 100;
          resolve(json.data);
        };
        xhr.onerror = () => reject(new Error('上传失败'));
        xhr.send(formData);
      });
    },
    async onPartImageChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.partUploadProgress = 1;
      try {
        const formData = new FormData();
        formData.append('image', file);
        const data = await this.uploadPartForm('/api/admin/parts/image', 'POST', formData);
        this.partForm.image_urls = [...this.partForm.image_urls, data.image_url];
        this.notify('success', '图片上传成功');
      } catch (e) {
        this.notify('error', e.message || '图片上传失败');
      } finally {
        this.partUploadProgress = 0;
        event.target.value = '';
      }
    },
    removePartImage(index) {
      const next = [...this.partForm.image_urls];
      next.splice(index, 1);
      this.partForm.image_urls = next;
    },
    async onPartVideoChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      if (file.size > 500 * 1024 * 1024) {
        event.target.value = '';
        this.notify('error', '视频大小最高 500M');
        return;
      }
      this.partForm.video_file = file;
      this.partUploadProgress = 0;
      this.partVideoUploading = true;
      try {
        const formData = new FormData();
        formData.append('video', file);
        const data = await this.uploadPartForm('/api/admin/parts/video', 'POST', formData);
        this.partForm.video_url = data.video_url;
        this.partForm.video_file = null;
        event.target.value = '';
        this.notify('success', '视频上传成功');
      } catch (e) {
        this.partForm.video_file = null;
        event.target.value = '';
        this.notify('error', e.message || '视频上传失败');
      } finally {
        this.partVideoUploading = false;
      }
    },
    resolveAssetUrl(url) {
      if (!url) return '';
      if (/^https?:\/\//.test(url)) return url;
      return `${API_BASE}${url}`;
    },
    openImagePreview(url, urls = null) {
      if (Array.isArray(urls) && urls.length) {
        this.previewImageUrls = urls;
        this.previewImageIndex = urls.indexOf(this.resolveAssetUrl(url)) || 0;
      } else {
        this.previewImageUrls = [this.resolveAssetUrl(url)];
        this.previewImageIndex = 0;
      }
    },
    closeImagePreview() {
      this.previewImageUrls = [];
      this.previewImageIndex = 0;
    },
    openVideoPreview(url) {
      this.previewVideoUrl = this.resolveAssetUrl(url);
    },
    closeVideoPreview() {
      this.previewVideoUrl = '';
    },
    async deletePart(id) {
      const ok = await this.confirm('确认删除该零件吗？');
      if (!ok) return;
      try {
        await this.request(`/api/admin/parts/${id}`, { method: 'DELETE' });
        this.notify('success', '零件删除成功');
        await Promise.all([this.fetchParts(), this.fetchDashboardStats()]);
      } catch (e) {
        this.notify('error', e.message || '零件删除失败');
      }
    },
    notify(type, message) {
      this.notice = { show: true, type, message };
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => {
        this.notice.show = false;
      }, 2200);
    },
    confirm(message) {
      return new Promise((resolve) => {
        this.confirmDialog = { show: true, message, resolve };
      });
    },
    hideConfirm() {
      const resolve = this.confirmDialog.resolve;
      this.confirmDialog = { show: false, message: '', resolve: null };
      if (resolve) resolve(false);
    },
    onConfirmOk() {
      const resolve = this.confirmDialog.resolve;
      this.confirmDialog = { show: false, message: '', resolve: null };
      if (resolve) resolve(true);
    }
  }
};
</script>

<style>
* { box-sizing: border-box; }
body { margin: 0; font-family: 'Microsoft YaHei', sans-serif; background: #f5f7fa; }
#app { min-height: 100vh; }
.login-wrap { min-height: 100vh; display: grid; grid-template-columns: minmax(520px, 1.18fr) minmax(420px, .82fr); background: #eef2f7; overflow: hidden; }
.login-brand-panel { position: relative; display: flex; align-items: center; justify-content: center; min-height: 100vh; color: #fff; background: #061527; }
.login-brand-panel::before { content: ""; position: absolute; inset: 0; background-image: radial-gradient(circle, rgba(50, 134, 230, .82) 0 2px, transparent 2.5px), radial-gradient(circle, rgba(50, 134, 230, .5) 0 1.5px, transparent 2px); background-size: 180px 180px, 96px 96px; background-position: 108px 118px, 20px 58px; opacity: .75; }
.login-brand-panel::after { content: ""; position: absolute; left: 0; right: 0; top: 56%; height: 1px; background: linear-gradient(90deg, transparent, rgba(55, 132, 220, .55), transparent); }
.brand-center { position: relative; z-index: 1; width: min(520px, 78%); text-align: center; }
.brand-emblem { width: 112px; height: 112px; margin: 0 auto 22px; display: flex; align-items: center; justify-content: center; border-radius: 50%; background: rgba(28, 91, 175, .16); box-shadow: 0 0 0 12px rgba(35, 101, 191, .08), inset 0 0 28px rgba(48, 128, 226, .16); }
.brand-emblem span { width: 42px; height: 42px; display: block; border: 2px solid #2f80ed; border-radius: 10px; transform: rotate(45deg); position: relative; box-shadow: 0 0 18px rgba(47, 128, 237, .55); }
.brand-emblem span::after { content: ""; position: absolute; inset: 9px; border: 2px solid rgba(255, 255, 255, .35); border-radius: 7px; }
.brand-center h1 { margin: 0; font-size: 42px; line-height: 1.18; font-weight: 800; text-shadow: 0 4px 0 rgba(0, 0, 0, .22); }
.brand-subtitle { margin-top: 8px; color: rgba(218, 230, 247, .72); font-family: Georgia, 'Times New Roman', serif; font-size: 13px; font-weight: 700; letter-spacing: 8px; }
.brand-divider { height: 1px; margin: 14px 0 20px; background: linear-gradient(90deg, transparent, rgba(63, 139, 234, .7), transparent); }
.brand-features { display: flex; justify-content: center; gap: 74px; }
.brand-feature { width: 74px; color: rgba(227, 239, 255, .88); font-size: 12px; }
.brand-feature p { margin: 10px 0 0; white-space: nowrap; }
.feature-icon { width: 42px; height: 42px; margin: 0 auto; display: flex; align-items: center; justify-content: center; border: 1px solid rgba(90, 151, 229, .52); border-radius: 9px; color: #2f80ed; background: rgba(17, 39, 72, .78); box-shadow: inset 0 0 16px rgba(64, 132, 224, .12); font-size: 13px; font-weight: 700; }
.login-form-panel { display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 48px; background: #e9eef4; }
.login-card { width: min(390px, 100%); background: transparent; border-radius: 0; padding: 0; box-shadow: none; }
.login-card h2 { margin: 0; color: #071323; font-size: 30px; line-height: 1.2; font-weight: 800; }
.login-subtitle { margin: 8px 0 34px; color: #77869a; font-size: 14px; }
.login-state { min-height: 44px; display: flex; align-items: center; margin-bottom: 26px; padding: 0 16px; border: 1px solid #a9dcd8; border-radius: 8px; color: #08786d; background: #d8efed; font-size: 14px; font-weight: 700; }
.login-state.error { color: #b42318; border-color: #f5b5ad; background: #fff0ee; }
.login-field { margin-bottom: 24px; }
.login-field label { display: block; margin-bottom: 12px; color: #0d1b2f; font-size: 14px; font-weight: 700; }
.login-field input { width: 100%; height: 42px; padding: 0; border: 0; border-bottom: 2px solid #c7d1de; border-radius: 0; background: transparent; color: #081425; font-size: 16px; font-weight: 700; outline: none; transition: border-color .2s ease, box-shadow .2s ease; }
.login-field input::placeholder { color: #9aa7b7; font-weight: 500; }
.login-field input:focus { border-bottom-color: #3478db; box-shadow: 0 8px 0 -7px rgba(52, 120, 219, .22); }
.login-button { width: 100%; height: 46px; margin-top: 2px; border: 0; border-radius: 5px; background: #3474d9; color: #fff; font-size: 16px; font-weight: 800; box-shadow: 0 12px 24px rgba(52, 116, 217, .18); transition: transform .18s ease, box-shadow .18s ease, background .18s ease; }
.login-button:hover { background: #2868cc; box-shadow: 0 16px 30px rgba(52, 116, 217, .24); transform: translateY(-1px); }
.login-button:disabled { cursor: not-allowed; opacity: .72; transform: none; box-shadow: none; }
.form-item { margin-bottom: 14px; }
.form-item label { display: block; margin-bottom: 6px; color: #555; }
input, select, button { height: 34px; border: 1px solid #d9d9d9; border-radius: 4px; padding: 0 10px; }
input, select { width: 100%; }
button { cursor: pointer; background: #fff; }
button.primary { background: #1677ff; color: #fff; border-color: #1677ff; }
button.primary:disabled { cursor: not-allowed; opacity: .55; }
button.danger { color: #cf1322; border-color: #ffccc7; }
.error { color: #cf1322; margin-top: 10px; }
.layout { display: flex; min-height: 100vh; }
.sidebar { width: 220px; background: #001529; color: #fff; padding: 16px 0; }
.brand { font-size: 18px; font-weight: 600; padding: 0 20px 18px; border-bottom: 1px solid rgba(255, 255, 255, .15); margin-bottom: 8px; }
.menu { padding: 12px 20px; cursor: pointer; }
.menu.active, .menu:hover { background: #1677ff; }
.content { flex: 1; padding: 16px; }
.topbar { display: flex; justify-content: space-between; align-items: center; background: #fff; padding: 12px 16px; border-radius: 6px; margin-bottom: 16px; }
.panel { background: #fff; padding: 16px; border-radius: 6px; }
.panel { animation: panelFade .28s ease; }
.toolbar { display: grid; grid-template-columns: repeat(5, minmax(120px, 1fr)); gap: 10px; margin-bottom: 12px; }
.form-grid { display: grid; grid-template-columns: repeat(4, minmax(120px, 1fr)); gap: 10px; }
.part-grid { grid-template-columns: repeat(3, minmax(120px, 1fr)); }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid #f0f0f0; padding: 8px; text-align: left; font-size: 14px; }
.part-thumb { width: 48px; height: 48px; display: block; object-fit: cover; border-radius: 6px; border: 1px solid #e5e7eb; background: #f8fafc; }
.modal-mask { position: fixed; inset: 0; background: rgba(0, 0, 0, .45); display: flex; align-items: center; justify-content: center; z-index: 999; }
.modal { width: 520px; background: #fff; border-radius: 8px; padding: 16px; }
.modal { box-shadow: 0 18px 44px rgba(19, 38, 77, .22); animation: modalIn .24s ease; }
.modal-wide { width: 720px; }
.modal h4 { margin: 0 0 14px; }
.modal-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
.modal-form-item { display: flex; align-items: center; margin-bottom: 10px; }
.modal-form-full { grid-column: 1 / -1; align-items: flex-start; }
.modal-form-item label { width: 88px; flex: 0 0 88px; color: #333; font-size: 14px; }
.modal-form-item input, .modal-form-item select { flex: 1; width: auto; }
.readonly-text { flex: 1; min-height: 36px; display: flex; align-items: center; color: #36527a; }
.category-picker-item { align-items: flex-start; }
.category-picker { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 10px; }
.category-picker-level { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.category-picker-level span { width: 42px; flex: 0 0 42px; color: #36527a; font-size: 13px; font-weight: 700; }
.category-picker-level button { height: 30px; padding: 0 12px; border-color: #dbe5f2; background: #f8fbff; color: #36527a; font-size: 13px; }
.category-picker-level button.active { border-color: #8db5ff; color: #2f6fd6; background: #eef5ff; }
.category-picker-level button.selected { border-color: #3b7cff; color: #fff; background: #3b7cff; }
.category-picked { padding-left: 42px; color: #667085; font-size: 13px; }
.image-picker { flex: 1; min-width: 0; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.upload-button { position: relative; width: auto !important; flex: 0 0 auto !important; height: 34px; padding: 0 12px; display: inline-flex; align-items: center; justify-content: center; border: 1px solid #1677ff; border-radius: 4px; color: #1677ff; background: #eef6ff; cursor: pointer; font-size: 14px; }
.upload-button input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.upload-name { color: #667085; font-size: 13px; }
.upload-hint { color: #98a2b3; font-size: 13px; }
.image-preview { width: 76px; height: 76px; object-fit: cover; border-radius: 7px; border: 1px solid #e4e7ec; background: #f8fafc; }
.clickable-thumb { cursor: zoom-in; }
.image-preview-mask { z-index: 1300; }
.image-preview-modal { position: relative; max-width: min(920px, 92vw); max-height: 88vh; }
.image-preview-modal img { display: block; max-width: 100%; max-height: 88vh; object-fit: contain; border-radius: 8px; background: #fff; box-shadow: 0 22px 60px rgba(0, 0, 0, .32); }
.video-preview-modal { position: relative; width: min(920px, 92vw); max-height: 88vh; }
.video-preview-modal video { display: block; width: 100%; max-height: 88vh; border-radius: 8px; background: #000; box-shadow: 0 22px 60px rgba(0, 0, 0, .32); }
.image-preview-close { position: absolute; right: -14px; top: -14px; width: 34px; height: 34px; border: 0; border-radius: 50%; background: #fff; color: #172231; font-size: 24px; line-height: 34px; box-shadow: 0 8px 24px rgba(0, 0, 0, .22); }
.upload-progress { flex: 1; min-width: 0; display: flex; align-items: center; gap: 10px; }
.upload-progress-bar { flex: 1; height: 8px; overflow: hidden; border-radius: 999px; background: #e7edf4; }
.upload-progress-bar span { display: block; height: 100%; border-radius: inherit; background: #3b7cff; transition: width .2s ease; }
.upload-progress em { min-width: 42px; color: #36527a; font-style: normal; font-size: 13px; }
.modal-actions { margin-top: 14px; display: flex; justify-content: flex-end; gap: 10px; }
.notice { position: fixed; top: 22px; right: 22px; z-index: 1200; min-width: 260px; max-width: 360px; padding: 14px 16px; border-radius: 10px; background: #fff; box-shadow: 0 16px 34px rgba(0, 0, 0, .16); border-left: 4px solid #1677ff; }
.notice.success { border-left-color: #16a34a; }
.notice.error { border-left-color: #dc2626; }
.notice-title { font-weight: 600; margin-bottom: 4px; }
.notice-text { color: #444; font-size: 14px; }
.confirm-modal { width: 420px; }
.confirm-text { margin: 8px 0 16px; color: #333; }
.fade-pop-enter-active, .fade-pop-leave-active { transition: all .24s ease; }
.fade-pop-enter, .fade-pop-leave-to { opacity: 0; transform: translateY(-8px) scale(.98); }
@keyframes modalIn {
  from { opacity: 0; transform: translateY(8px) scale(.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes panelFade {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
@media (max-width: 1200px) {
  .toolbar, .form-grid, .part-grid { grid-template-columns: repeat(2, minmax(120px, 1fr)); }
  .modal-wide { width: 92%; }
  .modal-form-grid { grid-template-columns: 1fr; }
}
@media (max-width: 900px) {
  .login-wrap { grid-template-columns: 1fr; }
  .login-brand-panel { min-height: 42vh; padding: 48px 18px; }
  .login-form-panel { min-height: 58vh; padding: 42px 24px; align-items: flex-start; }
  .brand-center { width: min(420px, 92%); }
  .brand-center h1 { font-size: 32px; }
  .brand-subtitle { letter-spacing: 4px; }
  .brand-features { gap: 28px; }
}
@media (max-width: 520px) {
  .login-brand-panel { min-height: 38vh; }
  .brand-emblem { width: 86px; height: 86px; margin-bottom: 16px; }
  .brand-emblem span { width: 34px; height: 34px; }
  .brand-center h1 { font-size: 28px; }
  .brand-subtitle { font-size: 11px; letter-spacing: 2px; }
  .brand-features { gap: 16px; }
  .brand-feature { width: 62px; }
  .feature-icon { width: 38px; height: 38px; }
  .login-form-panel { padding: 34px 22px; }
  .login-card h2 { font-size: 26px; }
}

/* ERP layout theme */
body { color: #001b44; background: #eef3f8; font-size: 14px; }
input, select, button, textarea { font-family: 'Microsoft YaHei', Arial, sans-serif; font-size: 14px; }
.layout { background: #eef3f8; }
.sidebar { width: 232px; background: #111827; padding: 0; box-shadow: inset -1px 0 0 rgba(255, 255, 255, .06); }
.brand { height: 88px; display: flex; align-items: center; gap: 12px; padding: 0 14px; margin: 0; border-bottom: 1px solid rgba(255, 255, 255, .14); }
.brand-mark { width: 38px; height: 38px; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; background: #3b7cff; color: #fff; font-size: 13px; font-weight: 800; letter-spacing: 0; }
.brand strong { display: block; color: #fff; font-size: 18px; line-height: 1.2; font-weight: 800; letter-spacing: 0; }
.brand small { display: block; margin-top: 4px; color: #a8bdd8; font-size: 12px; line-height: 1.2; font-weight: 400; }
.menu { height: 48px; margin: 0 8px 2px; padding: 0 16px; display: flex; align-items: center; gap: 10px; border-radius: 8px; color: #e8f1ff; font-size: 15px; font-weight: 700; }
.menu.active, .menu:hover { background: #1f2d42; color: #fff; }
.menu-icon { width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; background: rgba(59, 124, 255, .16); color: #d8e8ff; font-size: 13px; line-height: 1; }
.menu.active .menu-icon, .menu:hover .menu-icon { background: #3b7cff; color: #fff; }
.content { padding: 0 24px 24px; background: #eef3f8; }
.topbar { height: 68px; margin: 0 -24px 24px; padding: 0 24px; border-radius: 0; border-bottom: 1px solid #e5ebf2; box-shadow: none; }
.page-title { color: #001b44; font-size: 16px; font-weight: 800; }
.topbar-user { display: flex; align-items: center; gap: 12px; color: #36527a; font-size: 14px; }
.user-pill { height: 42px; padding: 0 18px; display: inline-flex; align-items: center; border: 1px solid #dbe5f2; border-radius: 22px; background: #f8fbff; color: #001b44; font-size: 14px; font-weight: 700; }
.topbar button { width: auto; height: 34px; border-color: #dbe5f2; color: #36527a; background: #fff; font-size: 14px; }
.panel { padding: 0; border: 1px solid #e7edf4; border-radius: 6px; box-shadow: 0 1px 2px rgba(16, 24, 40, .03); overflow: hidden; }
.panel h3 { height: 50px; margin: 0; padding: 0 18px; display: flex; align-items: center; border-bottom: 1px solid #e7edf4; color: #001b44; font-size: 15px; font-weight: 800; }
.panel p { padding: 0 18px 18px; color: #36527a; font-size: 14px; }
.stats-grid { padding: 18px; display: grid; grid-template-columns: repeat(2, minmax(180px, 260px)); gap: 14px; }
.stat-card { min-height: 96px; padding: 18px; border: 1px solid #e7edf4; border-radius: 6px; background: #f8fbff; }
.stat-card span { display: block; margin-bottom: 12px; color: #36527a; font-size: 14px; font-weight: 700; }
.stat-card strong { color: #001b44; font-size: 30px; line-height: 1; font-weight: 800; }
.toolbar { padding: 18px; margin: 0; border-bottom: 1px solid #edf1f6; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.toolbar input { width: 170px; flex: 0 0 170px; }
.toolbar select { width: 150px; flex: 0 0 150px; }
.toolbar button { flex: 0 0 auto; }
input, select { height: 36px; border-color: #dbe5f2; border-radius: 6px; color: #001b44; background: #fff; }
button { width: auto; height: 36px; border-color: #dbe5f2; border-radius: 6px; color: #001b44; background: #f8fbff; font-weight: 500; }
button.primary { background: #3b7cff; border-color: #3b7cff; color: #fff; }
button.danger { color: #dc2626; border-color: #ffd1d1; background: #fffafa; }
.pagination { margin: 0 18px 18px; display: flex; align-items: center; justify-content: flex-end; gap: 10px; color: #36527a; font-size: 14px; }
.pagination select { width: 96px; flex: 0 0 96px; }
.pagination button { padding: 0 14px; }
.pagination button:disabled { cursor: not-allowed; opacity: .45; }
table { margin: 0 18px 18px; width: calc(100% - 36px); border: 1px solid #e7edf4; border-radius: 8px; border-collapse: separate; border-spacing: 0; overflow: hidden; }
th { height: 42px; color: #001b44; background: #f8fafc; font-size: 13px; font-weight: 700; }
td { height: 46px; color: #001b44; background: #fff; font-size: 14px; }
th, td { padding: 8px 14px; border: 0; border-bottom: 1px solid #edf1f6; }
tbody tr:last-child td { border-bottom: 0; }
.category-level-badge { height: 24px; padding: 0 10px; display: inline-flex; align-items: center; border-radius: 12px; font-size: 12px; font-weight: 800; }
.category-level-badge.level-1 { color: #2f6fd6; background: #eef5ff; border: 1px solid #cfe0ff; }
.category-level-badge.level-2 { color: #16794f; background: #ecfdf3; border: 1px solid #bbf0d2; }
.category-level-badge.level-3 { color: #b76a00; background: #fff7e8; border: 1px solid #ffe1a8; }
.row-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.row-actions button:disabled { cursor: not-allowed; opacity: .45; }
.part-thumb { width: 40px; height: 40px; border-radius: 4px; }
.modal { border-radius: 8px; border: 1px solid #e7edf4; }
.modal h4 { color: #001b44; font-size: 16px; font-weight: 800; }
.modal-form-item label { color: #001b44; font-size: 14px; font-weight: 700; }
.notice { border-radius: 8px; color: #001b44; }
.thumb-list { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.image-preview-wrap { position: relative; display: inline-block; }
.image-preview-wrap .image-preview { cursor: pointer; }
.image-remove { position: absolute; right: -6px; top: -6px; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; border-radius: 50%; background: #dc2626; color: #fff; font-size: 12px; line-height: 1; cursor: pointer; }
.image-preview-nav { position: absolute; bottom: -36px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 12px; color: #fff; font-size: 14px; }
.image-preview-nav button { width: 32px; height: 32px; border: 0; border-radius: 50%; background: rgba(255,255,255,.18); color: #fff; font-size: 20px; line-height: 1; cursor: pointer; }
.image-preview-nav button:disabled { opacity: .35; cursor: not-allowed; }
.image-preview-count { min-width: 56px; text-align: center; font-size: 13px; color: #fff; }
</style>



